import { http } from './http';
import type {
  AccreditationReportDto,
  ApiKeyDto,
  ApprovalDto,
  ArchiveRecordDto,
  AuditEventDto,
  CertificateDto,
  CreateInvoiceInput,
  ErpConnectorDto,
  InboxItemDto,
  InvoiceDto,
  InvoiceTemplateDto,
  NotificationDto,
  PeppolParticipantDto,
  PeppolTransactionDto,
  RecoveryFindingDto,
  SignInRequest,
  SignInResponse,
  SpendOverviewDto,
  TddSubmissionDto,
  TenantDto,
  UserDto,
  ValidationResultDto,
  ValidationRuleDto,
  VatOverviewDto,
  WebhookEventDto,
} from './types';

/**
 * Low-level fetcher functions, grouped by resource. React Query hooks in
 * `./hooks/*.ts` wrap these — but other code (e.g. server-side rendering
 * later) can call them directly as well.
 */

// ---- auth ----

export const authApi = {
  signIn: (body: SignInRequest) => http().post<SignInResponse>('/auth/signin', body).then((r) => r.data),
  me: () => http().get<UserDto>('/auth/me').then((r) => r.data),
};

// ---- tenants ----

export const tenantsApi = {
  list: (params?: { status?: string; search?: string }) =>
    http().get<TenantDto[]>('/tenants', { params }).then((r) => r.data),
  get: (id: string) => http().get<TenantDto>(`/tenants/${id}`).then((r) => r.data),
  create: (input: Partial<TenantDto>) => http().post<TenantDto>('/tenants', input).then((r) => r.data),
  update: (id: string, patch: Partial<TenantDto>) =>
    http().patch<TenantDto>(`/tenants/${id}`, patch).then((r) => r.data),
  suspend: (id: string, reason?: string) =>
    http().post<TenantDto>(`/tenants/${id}/suspend`, { reason }).then((r) => r.data),
  terminate: (id: string) => http().post<TenantDto>(`/tenants/${id}/terminate`).then((r) => r.data),
  stats: (id: string) =>
    http()
      .get<{ invoiceCount: number; openApprovals: number; recoveryOpen: number }>(
        `/tenants/${id}/stats`,
      )
      .then((r) => r.data),
};

// ---- invoices ----

export interface InvoiceListFilter {
  tenantId?: string;
  status?: string;
  direction?: 'OUTBOUND' | 'INBOUND';
  search?: string;
  since?: string;
  until?: string;
  limit?: number;
}

export const invoicesApi = {
  list: (filter?: InvoiceListFilter) =>
    http().get<InvoiceDto[]>('/invoices', { params: filter }).then((r) => r.data),
  listForAdmin: (filter?: InvoiceListFilter) =>
    http().get<InvoiceDto[]>('/invoices/admin', { params: filter }).then((r) => r.data),
  get: (id: string) => http().get<InvoiceDto>(`/invoices/${id}`).then((r) => r.data),
  create: (input: CreateInvoiceInput) =>
    http().post<InvoiceDto>('/invoices', input).then((r) => r.data),
  acknowledge: (id: string) =>
    http().post<InvoiceDto>(`/invoices/${id}/acknowledge`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    http().post<InvoiceDto>(`/invoices/${id}/cancel`, { reason }).then((r) => r.data),
  countByStatus: () =>
    http()
      .get<Array<{ status: string; _count: { _all: number } }>>('/invoices/stats/by-status')
      .then((r) => r.data),
  /** Download the PINT-AE UBL XML as a Blob. */
  downloadXml: async (id: string) => {
    const res = await http().get(`/invoices/${id}/xml`, { responseType: 'blob' });
    return res.data as Blob;
  },
  /** Real PDF rendered server-side (pdfkit). */
  downloadPdf: async (id: string) => {
    const res = await http().get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    return res.data as Blob;
  },
  /** Print-ready HTML invoice (fallback / preview). */
  fetchPrintableHtml: async (id: string) => {
    const res = await http().get<string>(`/invoices/${id}/print`, { responseType: 'text' });
    return res.data;
  },
};

// ---- validation rules ----

export const rulesApi = {
  list: (params?: { tenantId?: string; status?: string }) =>
    http().get<ValidationRuleDto[]>('/rules', { params }).then((r) => r.data),
  get: (id: string) => http().get<ValidationRuleDto>(`/rules/${id}`).then((r) => r.data),
  create: (input: Partial<ValidationRuleDto>) =>
    http().post<ValidationRuleDto>('/rules', input).then((r) => r.data),
  update: (id: string, patch: Partial<ValidationRuleDto>) =>
    http().patch<ValidationRuleDto>(`/rules/${id}`, patch).then((r) => r.data),
  toggle: (id: string) =>
    http().post<ValidationRuleDto>(`/rules/${id}/toggle`).then((r) => r.data),
  remove: (id: string) => http().delete<void>(`/rules/${id}`).then(() => undefined),
  resultsFor: (invoiceId: string) =>
    http().get<ValidationResultDto[]>(`/invoices/${invoiceId}/validation`).then((r) => r.data),
};

// ---- peppol ----

export const peppolApi = {
  participants: (params?: { tenantId?: string }) =>
    http().get<PeppolParticipantDto[]>('/peppol/participants', { params }).then((r) => r.data),
  participantLookup: (participantId: string) =>
    http()
      .post<{ participant?: PeppolParticipantDto; endpoints: unknown[] }>(
        '/peppol/participants/lookup',
        { participantId },
      )
      .then((r) => r.data),
  registerParticipant: (input: {
    scheme: string;
    identifier: string;
    legalName: string;
    country: string;
    documentProfiles: string[];
    activate?: boolean;
    publishInDirectory?: boolean;
  }) =>
    http()
      .post<{ participant: PeppolParticipantDto; requiresKyc: boolean }>(
        '/peppol/participants/register',
        input,
      )
      .then((r) => r.data),
  activate: (participantId: string) =>
    http().post<void>(`/peppol/participants/${encodeURIComponent(participantId)}/activate`).then(() => undefined),
  publish: (participantId: string) =>
    http().post<void>(`/peppol/participants/${encodeURIComponent(participantId)}/publish`).then(() => undefined),
  unpublish: (participantId: string) =>
    http().post<void>(`/peppol/participants/${encodeURIComponent(participantId)}/unpublish`).then(() => undefined),
  deactivate: (participantId: string) =>
    http().post<void>(`/peppol/participants/${encodeURIComponent(participantId)}/deactivate`).then(() => undefined),
  deleteParticipant: (participantId: string) =>
    http().delete<void>(`/peppol/participants/${encodeURIComponent(participantId)}`).then(() => undefined),
  transactions: (params?: { direction?: string; state?: string; tenantId?: string; limit?: number }) =>
    http().get<PeppolTransactionDto[]>('/peppol/transactions', { params }).then((r) => r.data),
  transaction: (id: string) =>
    http().get<PeppolTransactionDto>(`/peppol/transactions/${id}`).then((r) => r.data),
  acknowledgeTransaction: (id: string) =>
    http().put<void>(`/peppol/transactions/${id}/acknowledge`).then(() => undefined),
};

// ---- webhooks (admin view) ----

export const webhooksApi = {
  list: (params?: { delivery?: string; limit?: number }) =>
    http().get<WebhookEventDto[]>('/webhooks', { params }).then((r) => r.data),
  get: (id: string) => http().get<WebhookEventDto>(`/webhooks/${id}`).then((r) => r.data),
  replay: (id: string) =>
    http().post<WebhookEventDto>(`/webhooks/${id}/replay`).then((r) => r.data),
};

// ---- TDD ----

export const tddApi = {
  list: (params?: { tenantId?: string; status?: string }) =>
    http().get<TddSubmissionDto[]>('/tdd', { params }).then((r) => r.data),
  get: (id: string) => http().get<TddSubmissionDto>(`/tdd/${id}`).then((r) => r.data),
  submit: (invoiceId: string) =>
    http().post<TddSubmissionDto>(`/tdd/${invoiceId}/submit`).then((r) => r.data),
  retry: (id: string) => http().post<TddSubmissionDto>(`/tdd/${id}/retry`).then((r) => r.data),
};

// ---- archive ----

export const archiveApi = {
  list: (params?: { tenantId?: string; limit?: number }) =>
    http().get<ArchiveRecordDto[]>('/archive', { params }).then((r) => r.data),
  get: (id: string) => http().get<ArchiveRecordDto>(`/archive/${id}`).then((r) => r.data),
  verify: (id: string) =>
    http()
      .post<{ ok: boolean; expectedSha256: string; actualSha256: string }>(`/archive/${id}/verify`)
      .then((r) => r.data),
};

// ---- approval ----

export const approvalsApi = {
  list: (params?: { state?: string; tenantId?: string }) =>
    http().get<ApprovalDto[]>('/approvals', { params }).then((r) => r.data),
  approve: (id: string, reason?: string) =>
    http().post<ApprovalDto>(`/approvals/${id}/approve`, { reason }).then((r) => r.data),
  reject: (id: string, reason: string) =>
    http().post<ApprovalDto>(`/approvals/${id}/reject`, { reason }).then((r) => r.data),
  cancel: (id: string) => http().post<ApprovalDto>(`/approvals/${id}/cancel`).then((r) => r.data),
};

// ---- inbox ----

export const inboxApi = {
  list: (params?: { category?: string; search?: string }) =>
    http().get<InboxItemDto[]>('/inbox', { params }).then((r) => r.data),
  get: (id: string) => http().get<InboxItemDto>(`/inbox/${id}`).then((r) => r.data),
  markRead: (id: string) => http().post<InboxItemDto>(`/inbox/${id}/read`).then((r) => r.data),
  dismiss: (id: string) => http().post<InboxItemDto>(`/inbox/${id}/dismiss`).then((r) => r.data),
};

// ---- templates ----

export const templatesApi = {
  list: () => http().get<InvoiceTemplateDto[]>('/templates').then((r) => r.data),
  get: (id: string) => http().get<InvoiceTemplateDto>(`/templates/${id}`).then((r) => r.data),
  create: (input: { name: string; payload: unknown }) =>
    http().post<InvoiceTemplateDto>('/templates', input).then((r) => r.data),
  update: (id: string, patch: Partial<InvoiceTemplateDto>) =>
    http().put<InvoiceTemplateDto>(`/templates/${id}`, patch).then((r) => r.data),
  remove: (id: string) => http().delete<void>(`/templates/${id}`).then(() => undefined),
};

// ---- notifications ----

export const notificationsApi = {
  list: () => http().get<NotificationDto[]>('/notifications').then((r) => r.data),
  markRead: (id: string) =>
    http().post<NotificationDto>(`/notifications/${id}/read`).then((r) => r.data),
};

// ---- audit ----

export const auditApi = {
  list: (params?: { tenantId?: string; resource?: string; since?: string; limit?: number }) =>
    http().get<AuditEventDto[]>('/audit', { params }).then((r) => r.data),
};

// ---- analytics ----

export const analyticsApi = {
  spend: (params?: { tenantId?: string }) =>
    http().get<SpendOverviewDto>('/analytics/spend', { params }).then((r) => r.data),
  vat: (params?: { tenantId?: string }) =>
    http().get<VatOverviewDto>('/analytics/vat', { params }).then((r) => r.data),
  recovery: (params?: { tenantId?: string; status?: string }) =>
    http().get<RecoveryFindingDto[]>('/analytics/recovery', { params }).then((r) => r.data),
  setRecoveryStatus: (id: string, status: string) =>
    http()
      .post<RecoveryFindingDto>(`/analytics/recovery/${id}/status`, { status })
      .then((r) => r.data),
};

// ---- certificates ----

export const certificatesApi = {
  list: (params?: { type?: string }) =>
    http().get<CertificateDto[]>('/certificates', { params }).then((r) => r.data),
  get: (id: string) => http().get<CertificateDto>(`/certificates/${id}`).then((r) => r.data),
  rotate: (id: string) => http().post<CertificateDto>(`/certificates/${id}/rotate`).then((r) => r.data),
  revoke: (id: string) => http().post<CertificateDto>(`/certificates/${id}/revoke`).then((r) => r.data),
};

// ---- reports ----

export const reportsApi = {
  list: (params?: { category?: string; since?: string; until?: string }) =>
    http().get<AccreditationReportDto[]>('/reports', { params }).then((r) => r.data),
  get: (id: string) => http().get<AccreditationReportDto>(`/reports/${id}`).then((r) => r.data),
  generate: (input: { category: string; period: string }) =>
    http().post<AccreditationReportDto>('/reports/generate', input).then((r) => r.data),
};

// ---- settings ----

export const settingsApi = {
  get: () =>
    http()
      .get<{
        invoiceNumberPrefix: string;
        invoiceNumberSeq: number;
        autoSubmitTdd: boolean;
        approvalThresholdAed: string;
        webhookUrl?: string | null;
      }>('/settings')
      .then((r) => r.data),
  update: (patch: Record<string, unknown>) =>
    http().put<void>('/settings', patch).then(() => undefined),
  apiKeys: () => http().get<ApiKeyDto[]>('/settings/api-keys').then((r) => r.data),
  createApiKey: (input: { name: string; scopes: string[] }) =>
    http()
      .post<ApiKeyDto & { plaintext: string }>('/settings/api-keys', input)
      .then((r) => r.data),
  revokeApiKey: (id: string) =>
    http().delete<void>(`/settings/api-keys/${id}`).then(() => undefined),
  erpConnectors: () => http().get<ErpConnectorDto[]>('/settings/erp-connectors').then((r) => r.data),
  upsertErpConnector: (kind: string, config: Record<string, unknown>) =>
    http().put<ErpConnectorDto>(`/settings/erp-connectors/${kind}`, config).then((r) => r.data),
};

// ---- health ----

export const healthApi = {
  ping: () =>
    http()
      .get<{ status: 'ok'; db: 'ok' | 'down'; uptime: number }>('/health')
      .then((r) => r.data),
};

// ---- M9 MFA ----

export interface MfaSetupResponse {
  secret: string;
  otpauthUri: string;
}

export const mfaApi = {
  setup: () => http().post<MfaSetupResponse>('/mfa/setup').then((r) => r.data),
  activate: (token: string) =>
    http().post<{ enabled: boolean; backupCodes: string[] }>('/mfa/activate', { token }).then((r) => r.data),
  regenerateBackupCodes: () =>
    http().post<{ backupCodes: string[] }>('/mfa/backup-codes/regenerate').then((r) => r.data),
  verify: (token: string) =>
    http().post<{ ok: boolean }>('/mfa/verify', { token }).then((r) => r.data),
  disable: () => http().delete<{ disabled: boolean }>('/mfa').then((r) => r.data),
};

export const authMfaApi = {
  verifyChallenge: (mfaChallengeToken: string, token: string) =>
    http().post<SignInResponse>('/auth/mfa/verify', { mfaChallengeToken, token }).then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    http()
      .post<{ changed: boolean }>('/auth/password', { currentPassword, newPassword })
      .then((r) => r.data),
};

// ---- M2 Onboarding + KYC + UBO ----

export interface OnboardingStateDto {
  id: string;
  tenantId: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'STOPPED' | 'ON_HOLD';
  currentStep: string;
  tradeLicenseNumber?: string | null;
  tradeLicenseAuthority?: string | null;
  tradeLicenseIssueDate?: string | null;
  tradeLicenseExpiry?: string | null;
  legalForm?: string | null;
  legalAddress?: string | null;
  vatStatus?: string | null;
  vatRegistrationDate?: string | null;
  ctStatus?: string | null;
  ctRegistrationNumber?: string | null;
  packageTier?: string | null;
  emaraVerificationId?: string | null;
  emaraVerifiedAt?: string | null;
  emaraChannel?: 'inbound' | 'outbound' | null;
  progressPercent: number;
  orderedSteps: string[];
}

export interface EmaraTaxAuthorizationDto {
  authorizationUrl: string;
  state: string;
  redirectUri: string;
}

export interface EmaraTaxVerificationDto {
  verified: boolean;
  emaraVerificationId: string;
  emaraVerifiedAt: string | null;
  channel: 'inbound' | 'outbound';
}

export interface UboDto {
  id: string;
  tenantId: string;
  fullName: string;
  nationality: string;
  dateOfBirth: string;
  residenceAddress: string;
  ownershipPercentage: number;
  idDocumentId?: string | null;
}

export interface KycDocumentDto {
  id: string;
  tenantId: string;
  documentType: string;
  status: string;
  storageKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  issueDate?: string | null;
  expiryDate?: string | null;
  uploadedAt: string;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  virusScanStatus: string;
}

export const onboardingApi = {
  getState: () => http().get<OnboardingStateDto>('/onboarding/state').then((r) => r.data),
  saveCompanyData: (patch: Partial<OnboardingStateDto>) =>
    http().post<OnboardingStateDto>('/onboarding/state/company-data', patch).then((r) => r.data),
  advance: () => http().post<{ currentStep: string }>('/onboarding/state/advance').then((r) => r.data),
  stop: (reason: string) =>
    http().post<OnboardingStateDto>('/onboarding/state/stop', { reason }).then((r) => r.data),
  authorizeEmaraTax: (callbackBase?: string) =>
    http()
      .post<EmaraTaxAuthorizationDto>('/onboarding/state/emaratax/authorize', { callbackBase })
      .then((r) => r.data),
  completeEmaraTax: (code: string, channel: 'inbound' | 'outbound') =>
    http()
      .post<EmaraTaxVerificationDto>('/onboarding/state/emaratax/callback', { code, channel })
      .then((r) => r.data),
  listUbos: () => http().get<UboDto[]>('/onboarding/ubo').then((r) => r.data),
  createUbo: (body: Omit<UboDto, 'id' | 'tenantId'>) =>
    http().post<UboDto>('/onboarding/ubo', body).then((r) => r.data),
  updateUbo: (id: string, body: Partial<UboDto>) =>
    http().put<UboDto>(`/onboarding/ubo/${id}`, body).then((r) => r.data),
  deleteUbo: (id: string) => http().delete<{ deleted: boolean }>(`/onboarding/ubo/${id}`).then((r) => r.data),
  validateUbo: () =>
    http().get<{ valid: boolean; sum: number; count: number }>('/onboarding/ubo/validate').then((r) => r.data),
  requiredKyc: () =>
    http().get<{ requiredTypes: string[] }>('/onboarding/kyc/required').then((r) => r.data),
  listKyc: () => http().get<KycDocumentDto[]>('/onboarding/kyc').then((r) => r.data),
  recordKyc: (body: {
    documentType: string;
    storageKey: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    issueDate?: string;
    expiryDate?: string;
  }) => http().post<KycDocumentDto>('/onboarding/kyc', body).then((r) => r.data),
};

export const onboardingAdminApi = {
  reviewQueue: () => http().get<KycDocumentDto[]>('/admin/onboarding/kyc-queue').then((r) => r.data),
  approveDocument: (documentId: string) =>
    http().post<KycDocumentDto>(`/admin/onboarding/kyc/${documentId}/approve`).then((r) => r.data),
  rejectDocument: (documentId: string, reason: string) =>
    http().post<KycDocumentDto>(`/admin/onboarding/kyc/${documentId}/reject`, { reason }).then((r) => r.data),
};

// ---- M6 Documents (active store) ----

export interface DocumentDto {
  id: string;
  tenantId: string;
  originalFilename: string;
  fileSizeBytes: number;
  mimeType: string;
  category: string;
  subCategory?: string | null;
  tags: string[];
  storageKey: string;
  uploadedAt: string;
  issueDate?: string | null;
  expiryDate?: string | null;
  retentionUntil?: string | null;
  bucketType: string;
  status: string;
  versionNumber: number;
  virusScanStatus: string;
}

export interface UploadIntentDto {
  storageKey: string;
  putUrl: string;
  expiresInSeconds: number;
  bucket: string;
  mimeType: string;
  sizeBytes: number;
}

export const documentsApi = {
  list: (params?: { category?: string; status?: string }) =>
    http().get<DocumentDto[]>('/documents', { params }).then((r) => r.data),
  get: (id: string) => http().get<DocumentDto>(`/documents/${id}`).then((r) => r.data),
  expiring: (days = 90) =>
    http().get<DocumentDto[]>('/documents/expiring', { params: { days } }).then((r) => r.data),
  uploadIntent: (body: { category: string; originalFilename: string; mimeType: string; sizeBytes: number }) =>
    http().post<UploadIntentDto>('/documents/upload-intent', body).then((r) => r.data),
  downloadUrl: (id: string) =>
    http().get<{ getUrl: string; expiresInSeconds: number; bucket: string }>(`/documents/${id}/download-url`).then((r) => r.data),
  record: (body: {
    category: string;
    subCategory?: string;
    originalFilename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey: string;
    issueDate?: string;
    expiryDate?: string;
    tags?: string[];
  }) => http().post<DocumentDto>('/documents/upload', body).then((r) => r.data),
  share: (
    id: string,
    body: {
      recipientName: string;
      recipientEmail?: string;
      expiresInDays: number;
      password?: string;
      ipAllowlist?: string[];
      downloadLimit?: number;
      recipientNote?: string;
    },
  ) =>
    http()
      .post<{ id: string; shareToken: string; expiresAt: string }>(`/documents/${id}/share`, body)
      .then((r) => r.data),
  listShares: (id: string) =>
    http().get<Array<{ id: string; expiresAt: string; revokedAt?: string | null; downloadCount: number }>>(`/documents/${id}/share-links`).then((r) => r.data),
};

// ---- M5 Tax filings ----

export interface TaxFilingDto {
  id: string;
  tenantId: string;
  filingType: 'VAT' | 'CT' | 'EXCISE';
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: string;
  outputVatStandardMinor: number;
  outputVatZeroMinor: number;
  outputVatExemptMinor: number;
  reverseChargeOutputMinor: number;
  inputVatRecoverableMinor: number;
  inputVatNonRecoverableMinor: number;
  reverseChargeInputMinor: number;
  adjustmentsMinor: number;
  adjustmentReason?: string | null;
  netPayableMinor: number;
  ctAccountingProfitMinor?: number | null;
  ctTaxableIncomeMinor?: number | null;
  ctTaxDueMinor?: number | null;
  ftaSubmissionId?: string | null;
  paymentStatus: string;
}

export const taxFilingApi = {
  list: (params?: { status?: string; type?: string }) =>
    http().get<TaxFilingDto[]>('/tax-filings', { params }).then((r) => r.data),
  get: (id: string) => http().get<TaxFilingDto>(`/tax-filings/${id}`).then((r) => r.data),
  clientApprove: (id: string) =>
    http().post<TaxFilingDto>(`/tax-filings/${id}/client-approve`).then((r) => r.data),
  clientReject: (id: string, reason: string) =>
    http().post<TaxFilingDto>(`/tax-filings/${id}/client-reject`, { reason }).then((r) => r.data),
};

export const taxFilingAdminApi = {
  list: (params?: { status?: string; type?: string }) =>
    http().get<TaxFilingDto[]>('/admin/tax-filings', { params }).then((r) => r.data),
  generateVat: (tenantId: string, body: { periodStart: string; periodEnd: string; dueDate?: string }) =>
    http().post<TaxFilingDto>(`/admin/tax-filings/tenants/${tenantId}/generate-vat`, body).then((r) => r.data),
  generateCt: (
    tenantId: string,
    body: { periodStart: string; periodEnd: string; dueDate?: string; accountingProfitMinor?: number },
  ) => http().post<TaxFilingDto>(`/admin/tax-filings/tenants/${tenantId}/generate-ct`, body).then((r) => r.data),
  addAdjustment: (id: string, deltaMinor: number, reason: string) =>
    http().post<TaxFilingDto>(`/admin/tax-filings/${id}/adjustment`, { deltaMinor, reason }).then((r) => r.data),
  taxAdvisorApprove: (id: string) =>
    http().post<TaxFilingDto>(`/admin/tax-filings/${id}/tax-advisor-approve`).then((r) => r.data),
  submit: (id: string) =>
    http().post<TaxFilingDto>(`/admin/tax-filings/${id}/submit-emaratax`).then((r) => r.data),
  recordPayment: (id: string, reference: string) =>
    http().post<TaxFilingDto>(`/admin/tax-filings/${id}/payment`, { reference }).then((r) => r.data),
};

// ---- M12 Compliance ----

export interface ComplianceMetricDto {
  checkType: string;
  label: string;
  currentValue: string;
  thresholdValue: string;
  status: 'OK' | 'WARNING' | 'CRITICAL';
  escalationStatus?: string | null;
  computedAt: string;
}

export interface RegulatoryInsuranceDto {
  id: string;
  insuranceType: string;
  policyNumber: string;
  insurer: string;
  effectiveFrom: string;
  effectiveTo: string;
  coverageAedMinor: number;
  documentId?: string | null;
}

export interface IsoCertificationDto {
  id: string;
  standard: string;
  certificateNumber: string;
  issuer: string;
  effectiveFrom: string;
  effectiveTo: string;
  scope?: string | null;
  documentId?: string | null;
}

export interface SecurityIncidentDto {
  id: string;
  detectedAt: string;
  severity: string;
  category: string;
  description: string;
  affectedTenantIds: string[];
  resolvedAt?: string | null;
  mttrMinutes?: number | null;
  rootCause?: string | null;
  mitigations?: string | null;
}

export interface CompliancePostureDto {
  dataResidency: {
    provider: string;
    region: string;
    regionName: string;
    note: string;
    excludedRegions: string[];
  };
  hsm: {
    provider: string;
    region: string;
    regionName: string;
    clusterId: string;
    fipsLevel: string;
    note: string;
  };
  pintAe: {
    specVersion: string;
    schematronVersion: string;
    schematronPublished: string;
    customizationId: string;
  };
  emaraTax: {
    aspOnboardingUrl: string;
    callbackPath: string;
    mtlsCommonName: string;
  };
}

export const complianceApi = {
  posture: () => http().get<CompliancePostureDto>('/compliance/posture').then((r) => r.data),
  dashboard: () => http().get<ComplianceMetricDto[]>('/admin/compliance/dashboard').then((r) => r.data),
  runChecks: () => http().post<ComplianceMetricDto[]>('/admin/compliance/checks/run-all').then((r) => r.data),
  insurances: () =>
    http().get<RegulatoryInsuranceDto[]>('/admin/compliance/insurances').then((r) => r.data),
  createInsurance: (body: Omit<RegulatoryInsuranceDto, 'id'>) =>
    http().post<RegulatoryInsuranceDto>('/admin/compliance/insurances', body).then((r) => r.data),
  iso: () => http().get<IsoCertificationDto[]>('/admin/compliance/iso').then((r) => r.data),
  createIso: (body: Omit<IsoCertificationDto, 'id'>) =>
    http().post<IsoCertificationDto>('/admin/compliance/iso', body).then((r) => r.data),
  incidents: () => http().get<SecurityIncidentDto[]>('/admin/compliance/incidents').then((r) => r.data),
  createIncident: (body: Pick<SecurityIncidentDto, 'detectedAt' | 'severity' | 'category' | 'description'> & {
    affectedTenantIds?: string[];
  }) => http().post<SecurityIncidentDto>('/admin/compliance/incidents', body).then((r) => r.data),
  resolveIncident: (id: string, rootCause: string, mitigations: string) =>
    http()
      .post<SecurityIncidentDto>(`/admin/compliance/incidents/${id}/resolve`, { rootCause, mitigations })
      .then((r) => r.data),
};

// ---- M3 FX rates ----

export interface FxRateDto {
  id: string;
  source: string;
  fromCurrency: string;
  toCurrency: string;
  rate: string;
  rateDate: string;
}

export const fxRateApi = {
  latest: () => http().get<FxRateDto[]>('/fx-rates/latest').then((r) => r.data),
  resolve: (from: string, on?: string) =>
    http()
      .get<{ from: string; to: 'AED'; rate: number | null }>('/fx-rates/resolve', { params: { from, on } })
      .then((r) => r.data),
  bootstrap: () => http().post<{ created: number }>('/fx-rates/bootstrap').then((r) => r.data),
};

// ---- M1 CRM ----

export interface CrmCompanyDto {
  id: string;
  tenantId: string;
  companyName: string;
  trn?: string | null;
  tradeLicenseNumber?: string | null;
  registrationNumber?: string | null;
  industry: string;
  companySize?: string | null;
  annualRevenueAed?: string | null;
  legalAddress?: string | null;
  billingAddress?: string | null;
  mailingAddress?: string | null;
  websiteUrl?: string | null;
  linkedinUrl?: string | null;
  customerType?: string | null;
  accountTier?: string | null;
  parentCompanyId?: string | null;
  parentCompany?: CrmCompanyDto | null;
  accountOwnerUserId?: string | null;
  timezone?: string | null;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactJobTitle?: string | null;
  primaryContactDepartment?: string | null;
  primaryContactDecisionRole?: string | null;
  primaryContactEmail: string;
  primaryContactEmailAlt?: string | null;
  primaryContactPhone?: string | null;
  primaryContactPhoneDirect?: string | null;
  primaryContactMobile?: string | null;
  primaryContactLinkedin?: string | null;
  primaryContactPreferredLanguage?: string | null;
  primaryContactPreferredChannel?: string | null;
  newsletterOptOut?: boolean;
  lastInteractionAt?: string | null;
}

export interface LeadDto {
  id: string;
  tenantId: string;
  companyId?: string | null;
  company?: CrmCompanyDto;
  leadSource: string;
  subjectOfInterest?: string | null;
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'DISQUALIFIED' | 'CONVERTED';
  disqualificationReason?: string | null;
  assignedToUserId: string;
  bantBudget: string;
  bantAuthority: string;
  bantNeed: string;
  bantTimeline: string;
  meddpiccMetrics?: string | null;
  meddpiccEconomicBuyer?: string | null;
  meddpiccDecisionCriteria?: string | null;
  meddpiccDecisionProcess?: string | null;
  meddpiccPaperProcess?: string | null;
  meddpiccIdentifyPain?: string | null;
  meddpiccChampion?: string | null;
  meddpiccCompetition?: string | null;
  leadScore: number;
  estimatedValueMinor?: string | null;
  estimatedMrrMinor?: string | null;
  estimatedArrMinor?: string | null;
  expectedCloseDate?: string | null;
  probability?: number | null;
  lossReason?: string | null;
  notes?: string | null;
  duplicateOfLeadId?: string | null;
  duplicateSimilarityScore?: number | null;
  createdAt: string;
  updatedAt: string;
  activities?: CrmActivityDto[];
}

export interface OpportunityDto {
  id: string;
  tenantId: string;
  companyId: string;
  company?: CrmCompanyDto;
  dealName: string;
  ownerUserId: string;
  amountMinor: number;
  currency: string;
  probability: number;
  expectedCloseDate: string;
  pipelineStage: 'DISCOVERY' | 'DEMO' | 'PROPOSAL' | 'NEGOTIATION' | 'CLOSED_WON' | 'CLOSED_LOST';
  packageTier?: string | null;
  lossReason?: string | null;
  winReason?: string | null;
  competitor?: string | null;
}

export interface CrmActivityDto {
  id: string;
  tenantId: string;
  activityType: 'CALL' | 'MEETING' | 'EMAIL' | 'DEMO' | 'PROPOSAL' | 'NOTE';
  relatedLeadId?: string | null;
  relatedOpportunityId?: string | null;
  relatedCompanyId?: string | null;
  activityTimestamp: string;
  subject?: string | null;
  outcome?: string | null;
  aiSummary?: string | null;
  sentimentScore?: number | null;
  emailMessageId?: string | null;
  emailThreadId?: string | null;
  emailDirection?: string | null;
  emailFrom?: string | null;
  emailTo?: string[];
  isSystemEvent?: boolean;
  metadata?: Record<string, unknown> | null;
  recordedById: string;
  durationMinutes?: number | null;
}

export interface SalesForecastRow {
  ownerUserId: string;
  weightedAedMinor: string;
  opportunityCount: number;
}

export interface FunnelMetricRow {
  stage: OpportunityDto['pipelineStage'];
  count: number;
  conversionRate: number | null;
}

export interface VelocityDto {
  avgDealMinor: string;
  winRate: number;
  dealsClosed: number;
  avgCycleDays: number;
  velocityAedMinorPerDay: string;
}

// ---- M4 Accounting ----

export interface BankAccountDto {
  id: string;
  tenantId: string;
  bankName: string;
  accountNumberEncrypted: string;
  ibanEncrypted?: string | null;
  swift?: string | null;
  currency: string;
  openingBalanceMinor: number;
  isActive: boolean;
  createdAt: string;
}

export interface BankTransactionDto {
  id: string;
  tenantId: string;
  bankAccountId: string;
  transactionDate: string;
  valueDate: string;
  amountMinor: number;
  currency: string;
  description?: string | null;
  counterpartyIban?: string | null;
  counterpartyName?: string | null;
  category?: string | null;
  status: string;
  matchedInvoiceId?: string | null;
  importedAt: string;
  importSource: string;
}

export interface AgingBucketDto {
  label: string;
  minDays: number;
  maxDays: number;
  amountMinor: string;
  invoiceCount: number;
}

export interface GlAccountDto {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  accountType: string;
  accountSubType?: string | null;
  parentAccountId?: string | null;
  isPostable: boolean;
  currency: string;
  createdAt: string;
}

export const accountingApi = {
  banks: () => http().get<BankAccountDto[]>('/accounting/banks').then((r) => r.data),
  autoMatch: () =>
    http().post<{ matched: number }>('/accounting/transactions/auto-match').then((r) => r.data),
  glAccounts: () => http().get<GlAccountDto[]>('/accounting/gl-accounts').then((r) => r.data),
  createGlAccount: (body: {
    code: string;
    name: string;
    accountType: 'Asset' | 'Liability' | 'Equity' | 'Income' | 'Expense';
    accountSubType?: string;
    parentAccountId?: string;
    isPostable?: boolean;
    currency?: string;
  }) => http().post<GlAccountDto>('/accounting/gl-accounts', body).then((r) => r.data),
  createBank: (body: { bankName: string; accountNumber: string; iban?: string; swift?: string; currency?: string }) =>
    http().post<BankAccountDto>('/accounting/banks', body).then((r) => r.data),
  transactions: (params?: { status?: string; bankAccountId?: string }) =>
    http().get<BankTransactionDto[]>('/accounting/transactions', { params }).then((r) => r.data),
  importTransactions: (
    bankAccountId: string,
    rows: Array<{
      transactionDate: string;
      valueDate?: string;
      amountMinor: number;
      currency?: string;
      description?: string;
      counterpartyIban?: string;
      counterpartyName?: string;
    }>,
    importSource?: 'MT940' | 'CSV' | 'MANUAL' | 'OPEN_BANKING',
  ) =>
    http()
      .post<{ imported: number }>(`/accounting/banks/${bankAccountId}/import`, { rows, importSource })
      .then((r) => r.data),
  categorize: (transactionId: string, body: { category: string; matchedInvoiceId?: string }) =>
    http().post<BankTransactionDto>(`/accounting/transactions/${transactionId}/categorize`, body).then((r) => r.data),
  post: (transactionId: string) =>
    http().post<BankTransactionDto>(`/accounting/transactions/${transactionId}/post`).then((r) => r.data),
  aging: () => http().get<{ ar: AgingBucketDto[]; ap: AgingBucketDto[] }>('/accounting/aging').then((r) => r.data),
  postingTemplates: () => http().get<unknown[]>('/accounting/posting-templates').then((r) => r.data),
  balanceSheet: (asOf?: string) =>
    http().get<BalanceSheetDto>('/accounting/reports/balance-sheet', { params: asOf ? { asOf } : {} }).then((r) => r.data),
  profitLoss: (params?: { periodStart?: string; periodEnd?: string }) =>
    http().get<ProfitLossDto>('/accounting/reports/profit-loss', { params }).then((r) => r.data),
  customerLedger: () =>
    http().get<CustomerLedgerRowDto[]>('/accounting/reports/customer-ledger').then((r) => r.data),
  supplierLedger: () =>
    http().get<SupplierLedgerRowDto[]>('/accounting/reports/supplier-ledger').then((r) => r.data),
};

export interface LedgerRowDto {
  code: string;
  name: string;
  balanceMinor: string;
}

export interface LedgerGroupDto {
  rows: LedgerRowDto[];
  totalMinor: string;
}

export interface BalanceSheetDto {
  asOf: string;
  assets: LedgerGroupDto;
  liabilities: LedgerGroupDto;
  equity: LedgerGroupDto;
}

export interface ProfitLossDto {
  periodStart: string;
  periodEnd: string;
  income: LedgerGroupDto;
  expense: LedgerGroupDto;
  netIncomeMinor: string;
}

export interface CustomerLedgerRowDto {
  buyerTrn: string;
  buyerName: string;
  invoiceCount: number;
  billedMinor: string;
  openMinor: string;
  oldestOpenIssueDate: string | null;
  daysOldest: number | null;
}

export interface SupplierLedgerRowDto {
  sellerTrn: string;
  sellerName: string;
  invoiceCount: number;
  billedMinor: string;
  openMinor: string;
  oldestOpenIssueDate: string | null;
  daysOldest: number | null;
}

// ---- M8 Tasks ----

export interface TaskDto {
  id: string;
  tenantId: string;
  title: string;
  description?: string | null;
  taskType: string;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  assigneeUserId: string;
  assignerUserId: string;
  priority: string;
  dueDate?: string | null;
  tags: string[];
  status: string;
  completionComment?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboxBucketDto {
  category: 'OVERDUE' | 'TODAY' | 'THIS_WEEK' | 'LATER' | 'NO_DUE';
  count: number;
}

export const tasksApi = {
  list: (scope: 'mine' | 'team' | 'all' = 'mine') =>
    http().get<TaskDto[]>('/tasks', { params: { scope } }).then((r) => r.data),
  buckets: () => http().get<InboxBucketDto[]>('/tasks/inbox-buckets').then((r) => r.data),
  get: (id: string) => http().get<TaskDto>(`/tasks/${id}`).then((r) => r.data),
  create: (body: {
    title: string;
    description?: string;
    taskType?: string;
    relatedEntityType?: string;
    relatedEntityId?: string;
    assigneeUserId: string;
    priority?: string;
    dueDate?: string;
    tags?: string[];
  }) => http().post<TaskDto>('/tasks', body).then((r) => r.data),
  transition: (id: string, status: string, completionComment?: string) =>
    http().post<TaskDto>(`/tasks/${id}/transition`, { status, completionComment }).then((r) => r.data),
  comment: (id: string, body: string) =>
    http().post<{ id: string; body: string; createdAt: string }>(`/tasks/${id}/comments`, { body }).then((r) => r.data),
};

// ---- M11 Notification preferences ----

export interface NotificationPreferenceDto {
  id: string;
  userId: string;
  category: string;
  channelEmail: boolean;
  channelSms: boolean;
  channelPush: boolean;
  channelInApp: boolean;
  mode: 'IMMEDIATE' | 'DAILY_DIGEST' | 'WEEKLY_DIGEST';
  dndStart?: string | null;
  dndEnd?: string | null;
  dndExceptUrgent: boolean;
  newsletterSubscribed: boolean;
}

// ---- M10 ERP connectors ----

export interface ErpVendorMeta {
  vendor: string;
  fields: string[];
  label: string;
}

export interface ErpConnectorFullDto {
  id: string;
  tenantId: string;
  name: string;
  vendor: string;
  status: string;
  lastSyncAt?: string | null;
  configJson: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export const erpApi = {
  vendors: () => http().get<ErpVendorMeta[]>('/erp-connectors/vendors').then((r) => r.data),
  list: () => http().get<ErpConnectorFullDto[]>('/erp-connectors').then((r) => r.data),
  create: (body: { vendor: string; name: string; config: Record<string, string> }) =>
    http().post<ErpConnectorFullDto>('/erp-connectors', body).then((r) => r.data),
  test: (id: string) =>
    http().post<ErpConnectorFullDto>(`/erp-connectors/${id}/test`).then((r) => r.data),
  sync: (id: string) =>
    http().post<{ connector: ErpConnectorFullDto; synced: number }>(`/erp-connectors/${id}/sync`).then((r) => r.data),
  remove: (id: string) =>
    http().delete<{ deleted: boolean }>(`/erp-connectors/${id}`).then((r) => r.data),
};

// ---- M11 Newsletter ----

export interface NewsletterCampaignDto {
  id: string;
  tenantId: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string | null;
  audience: unknown;
  scheduledFor?: string | null;
  sentAt?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const newsletterApi = {
  list: () => http().get<NewsletterCampaignDto[]>('/newsletter').then((r) => r.data),
  create: (body: { subject: string; bodyHtml: string; bodyText?: string; audience?: Record<string, unknown> }) =>
    http().post<NewsletterCampaignDto>('/newsletter', body).then((r) => r.data),
  send: (id: string) =>
    http().post<NewsletterCampaignDto>(`/newsletter/${id}/send`).then((r) => r.data),
  schedule: (id: string, scheduledFor: string) =>
    http().post<NewsletterCampaignDto>(`/newsletter/${id}/schedule`, { scheduledFor }).then((r) => r.data),
};

// ---- M11 Chat ----

export interface ChatThreadDto {
  id: string;
  tenantId: string;
  customerScopeKey?: string | null;
  title?: string | null;
  lastMessageAt?: string | null;
  createdAt: string;
}

export interface ChatMessageDto {
  id: string;
  threadId: string;
  senderUserId: string;
  body?: string | null;
  attachments?: unknown;
  isEdited: boolean;
  isDeleted: boolean;
  createdAt: string;
}

export const chatApi = {
  listThreads: () => http().get<ChatThreadDto[]>('/chat/threads').then((r) => r.data),
  ensureThread: (body?: { customerScopeKey?: string; title?: string }) =>
    http().post<ChatThreadDto>('/chat/threads/ensure', body ?? {}).then((r) => r.data),
  messages: (threadId: string) =>
    http().get<ChatMessageDto[]>(`/chat/threads/${threadId}/messages`).then((r) => r.data),
  post: (threadId: string, body: string) =>
    http().post<ChatMessageDto>(`/chat/threads/${threadId}/messages`, { body }).then((r) => r.data),
  markRead: (threadId: string) =>
    http().post<{ lastReadAt: string }>(`/chat/threads/${threadId}/mark-read`).then((r) => r.data),
};

export const notificationPreferencesApi = {
  list: () => http().get<NotificationPreferenceDto[]>('/notifications/preferences').then((r) => r.data),
  update: (category: string, patch: Partial<Omit<NotificationPreferenceDto, 'id' | 'userId' | 'category'>>) =>
    http().put<NotificationPreferenceDto>(`/notifications/preferences/${category}`, patch).then((r) => r.data),
};

export const crmApi = {
  listLeads: (params?: { status?: string; assignedToUserId?: string }) =>
    http().get<LeadDto[]>('/crm/leads', { params }).then((r) => r.data),
  createLead: (body: Partial<LeadDto> & { companyName: string; primaryContactFirstName: string; primaryContactLastName: string; primaryContactEmail: string; assignedToUserId?: string }) =>
    http().post<LeadDto>('/crm/leads', body).then((r) => r.data),
  getLead: (id: string) => http().get<LeadDto>(`/crm/leads/${id}`).then((r) => r.data),
  updateLead: (
    id: string,
    patch: Partial<
      Pick<
        LeadDto,
        | 'bantBudget'
        | 'bantAuthority'
        | 'bantNeed'
        | 'bantTimeline'
        | 'notes'
        | 'assignedToUserId'
        | 'subjectOfInterest'
        | 'leadScore'
        | 'estimatedValueMinor'
        | 'estimatedMrrMinor'
        | 'estimatedArrMinor'
        | 'expectedCloseDate'
        | 'probability'
        | 'lossReason'
        | 'meddpiccMetrics'
        | 'meddpiccEconomicBuyer'
        | 'meddpiccDecisionCriteria'
        | 'meddpiccDecisionProcess'
        | 'meddpiccPaperProcess'
        | 'meddpiccIdentifyPain'
        | 'meddpiccChampion'
        | 'meddpiccCompetition'
      >
    >,
  ) => http().patch<LeadDto>(`/crm/leads/${id}`, patch).then((r) => r.data),
  transitionLead: (id: string, status: LeadDto['status'], reason?: string) =>
    http().post<LeadDto>(`/crm/leads/${id}/transition`, { status, reason }).then((r) => r.data),
  convertLead: (
    id: string,
    body: { ownerUserId: string; dealName: string; amountMinor: number; currency?: string; expectedCloseDate: string; packageTier?: string },
  ) => http().post<OpportunityDto>(`/crm/leads/${id}/convert`, body).then((r) => r.data),
  duplicateSearch: (body: { companyName: string; trn?: string; email?: string; phone?: string }) =>
    http().post<Array<{ leadId: string; similarity: number }>>('/crm/leads/duplicates/search', body).then((r) => r.data),
  listOpportunities: (params?: { stage?: string; ownerUserId?: string }) =>
    http().get<OpportunityDto[]>('/crm/opportunities', { params }).then((r) => r.data),
  getOpportunity: (id: string) => http().get<OpportunityDto>(`/crm/opportunities/${id}`).then((r) => r.data),
  setStage: (id: string, stage: OpportunityDto['pipelineStage'], reason?: string) =>
    http().post<OpportunityDto>(`/crm/opportunities/${id}/stage`, { stage, reason }).then((r) => r.data),
  handOffToOnboarding: (opportunityId: string) =>
    http()
      .post<{ tenantId: string; opportunityId: string }>(`/crm/opportunities/${opportunityId}/hand-off`)
      .then((r) => r.data),
  listActivities: (params?: { leadId?: string; opportunityId?: string }) =>
    http().get<CrmActivityDto[]>('/crm/activities', { params }).then((r) => r.data),
  createActivity: (body: Partial<CrmActivityDto> & { activityType: CrmActivityDto['activityType'] }) =>
    http().post<CrmActivityDto>('/crm/activities', body).then((r) => r.data),
  listCompanies: (params?: { search?: string }) =>
    http().get<CrmCompanyDto[]>('/crm/companies', { params }).then((r) => r.data),
  forecast: (periodEnd?: string) =>
    http().get<SalesForecastRow[]>('/crm/reports/forecast', { params: periodEnd ? { periodEnd } : {} }).then((r) => r.data),
  funnel: () => http().get<FunnelMetricRow[]>('/crm/reports/funnel').then((r) => r.data),
  velocity: () => http().get<VelocityDto>('/crm/reports/velocity').then((r) => r.data),
};

// ---- M8 Workflow configurator ----

export type WorkflowTrigger =
  | 'MANUAL'
  | 'INVOICE_CREATED'
  | 'INVOICE_RECEIVED'
  | 'PERIOD_END'
  | 'DOCUMENT_UPLOADED'
  | 'KYC_SUBMITTED'
  | 'TAX_FILING_READY'
  | 'CUSTOM';

export interface WorkflowStepConfigDto {
  name: string;
  assigneeRule: string;
  dueDays?: number;
  action: string;
  escalation?: string;
}

export interface WorkflowDefinitionDto {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  triggerEvent: WorkflowTrigger;
  triggerCondition?: string | null;
  scope: 'TENANT' | 'CUSTOMER';
  stepsConfig: WorkflowStepConfigDto[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { instances: number };
}

export interface WorkflowInstanceDto {
  id: string;
  tenantId: string;
  workflowDefinitionId: string;
  triggerEntityType: string;
  triggerEntityId: string;
  status: string;
  currentStepNumber: number;
  startedAt: string;
  completedAt?: string | null;
  definition?: { id: string; name: string };
  tasks?: Array<{ id: string; status: string }>;
}

export const workflowApi = {
  triggers: () => http().get<WorkflowTrigger[]>('/workflow/triggers').then((r) => r.data),
  list: () => http().get<WorkflowDefinitionDto[]>('/workflow/definitions').then((r) => r.data),
  get: (id: string) =>
    http().get<WorkflowDefinitionDto>(`/workflow/definitions/${id}`).then((r) => r.data),
  create: (body: {
    name: string;
    description?: string;
    triggerEvent: WorkflowTrigger;
    triggerCondition?: string;
    scope?: 'TENANT' | 'CUSTOMER';
    steps: WorkflowStepConfigDto[];
  }) => http().post<WorkflowDefinitionDto>('/workflow/definitions', body).then((r) => r.data),
  update: (
    id: string,
    patch: Partial<{
      name: string;
      description: string;
      triggerEvent: WorkflowTrigger;
      triggerCondition: string;
      scope: 'TENANT' | 'CUSTOMER';
      steps: WorkflowStepConfigDto[];
      isActive: boolean;
    }>,
  ) => http().patch<WorkflowDefinitionDto>(`/workflow/definitions/${id}`, patch).then((r) => r.data),
  remove: (id: string) =>
    http().delete<{ deleted: boolean }>(`/workflow/definitions/${id}`).then((r) => r.data),
  instances: () => http().get<WorkflowInstanceDto[]>('/workflow/instances').then((r) => r.data),
  start: (id: string, body: { triggerEntityType: string; triggerEntityId: string }) =>
    http().post<WorkflowInstanceDto>(`/workflow/definitions/${id}/start`, body).then((r) => r.data),
};

// ---- M9 IdP federation ----

export type IdpType =
  | 'MICROSOFT_365'
  | 'GOOGLE'
  | 'OKTA'
  | 'AZURE_AD'
  | 'SAML2_GENERIC'
  | 'OIDC_GENERIC';

export interface IdpConnectionDto {
  id: string;
  tenantId: string;
  idpType: IdpType;
  displayName: string;
  metadataUrl?: string | null;
  clientId?: string | null;
  hasClientSecret: boolean;
  attributeMapping: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const idpApi = {
  types: () => http().get<IdpType[]>('/idp/types').then((r) => r.data),
  list: () => http().get<IdpConnectionDto[]>('/idp').then((r) => r.data),
  get: (id: string) => http().get<IdpConnectionDto>(`/idp/${id}`).then((r) => r.data),
  upsert: (body: {
    idpType: IdpType;
    displayName: string;
    metadataUrl?: string;
    clientId?: string;
    clientSecret?: string;
    attributeMapping?: Record<string, unknown>;
    isActive?: boolean;
  }) => http().post<IdpConnectionDto>('/idp', body).then((r) => r.data),
  update: (
    id: string,
    patch: Partial<{
      idpType: IdpType;
      displayName: string;
      metadataUrl: string;
      clientId: string;
      clientSecret: string;
      attributeMapping: Record<string, unknown>;
      isActive: boolean;
    }>,
  ) => http().patch<IdpConnectionDto>(`/idp/${id}`, patch).then((r) => r.data),
  setActive: (id: string, isActive: boolean) =>
    http().post<IdpConnectionDto>(`/idp/${id}/active`, { isActive }).then((r) => r.data),
  remove: (id: string) => http().delete<{ deleted: boolean }>(`/idp/${id}`).then((r) => r.data),
};

// ---- M12 Security incidents (extended — works against the SecurityIncidentDto above) ----

export type IncidentSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type IncidentCategory =
  | 'PHISHING'
  | 'CREDENTIAL_THEFT'
  | 'DATA_EXFILTRATION'
  | 'INSIDER_THREAT'
  | 'DDoS'
  | 'MALWARE'
  | 'MISCONFIGURATION'
  | 'OTHER';

export interface IncidentStatsDto {
  total: number;
  open: number;
  resolved: number;
  bySeverity: Record<IncidentSeverity, number>;
  avgMttrMinutes: number | null;
}

export const securityIncidentApi = {
  severities: () =>
    http().get<IncidentSeverity[]>('/admin/security-incidents/severities').then((r) => r.data),
  categories: () =>
    http().get<IncidentCategory[]>('/admin/security-incidents/categories').then((r) => r.data),
  stats: () => http().get<IncidentStatsDto>('/admin/security-incidents/stats').then((r) => r.data),
  list: (params?: { severity?: IncidentSeverity; status?: 'OPEN' | 'RESOLVED' }) =>
    http()
      .get<SecurityIncidentDto[]>('/admin/security-incidents', { params })
      .then((r) => r.data),
  get: (id: string) =>
    http().get<SecurityIncidentDto>(`/admin/security-incidents/${id}`).then((r) => r.data),
  create: (body: {
    detectedAt: string;
    severity: IncidentSeverity;
    category: IncidentCategory;
    description: string;
    affectedTenantIds?: string[];
    rootCause?: string;
    mitigations?: string;
  }) => http().post<SecurityIncidentDto>('/admin/security-incidents', body).then((r) => r.data),
  update: (id: string, patch: Partial<SecurityIncidentDto>) =>
    http().patch<SecurityIncidentDto>(`/admin/security-incidents/${id}`, patch).then((r) => r.data),
  resolve: (id: string, body: { rootCause?: string; mitigations?: string }) =>
    http()
      .post<SecurityIncidentDto>(`/admin/security-incidents/${id}/resolve`, body)
      .then((r) => r.data),
};

// ---- M9 Custom roles ----

export type RolePermissions = Record<string, Record<string, boolean>>;

export interface CustomRoleDto {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  permissions: RolePermissions;
  createdAt: string;
  updatedAt: string;
}

export interface RoleCatalogDto {
  modules: string[];
  actions: string[];
}

export const customRoleApi = {
  catalog: () => http().get<RoleCatalogDto>('/custom-roles/catalog').then((r) => r.data),
  list: () => http().get<CustomRoleDto[]>('/custom-roles').then((r) => r.data),
  get: (id: string) => http().get<CustomRoleDto>(`/custom-roles/${id}`).then((r) => r.data),
  create: (body: { name: string; description?: string; permissions: RolePermissions }) =>
    http().post<CustomRoleDto>('/custom-roles', body).then((r) => r.data),
  update: (
    id: string,
    patch: Partial<{ name: string; description: string; permissions: RolePermissions }>,
  ) => http().patch<CustomRoleDto>(`/custom-roles/${id}`, patch).then((r) => r.data),
  remove: (id: string) =>
    http().delete<{ deleted: boolean }>(`/custom-roles/${id}`).then((r) => r.data),
};
